import { supabase } from "@/integrations/supabase/client";
import { ORDER_STATUS } from "@/constants/orderConstants";
import { buildOrderNumber, type OrderSource } from "@/lib/orderNumberGenerator";
import { DEFAULT_ORDER_ID_CONFIG, type OrderIdConfig, type UserSettings } from "@/hooks/useUserSettings";
import { toZonedTime } from "date-fns-tz";
import { createActivityEntry } from "@/lib/deliveryLogFormat";
import { appendNoteAtomic } from "@/lib/atomicNoteAppend";

/**
 * Business logic hook for order operations
 * Separates data layer from UI components
 */
export const useOrderMutations = () => {
  /**
   * Check if an order number already exists
   */
  const checkDuplicateOrder = async (orderNum: string): Promise<boolean> => {
    try {
      const { data: existingOrders } = await supabase
        .from("orders")
        .select("id")
        .eq("order_number", orderNum)
        .limit(1);

      return existingOrders && existingOrders.length > 0;
    } catch (error) {
      console.error("Error checking duplicate order:", error);
      return false;
    }
  };

  /**
   * Find existing customer by phone or name, or create new customer
   * Returns customer ID or null
   */
  const findOrCreateCustomer = async (
    customerName: string,
    customerPhone?: string
  ): Promise<string | null> => {
    if (!customerName) return null;

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Try to find by phone first (more unique) - explicitly filter by user_id
      if (customerPhone) {
        const { data: customersByPhone } = await supabase
          .from("customers")
          .select("id")
          .eq("user_id", user.id)
          .eq("phone", customerPhone)
          .limit(1);

        if (customersByPhone && customersByPhone.length > 0) {
          return customersByPhone[0].id;
        }
      }

      // Try to find by name - explicitly filter by user_id
      const { data: customersByName } = await supabase
        .from("customers")
        .select("id")
        .eq("user_id", user.id)
        .eq("name", customerName)
        .limit(1);

      if (customersByName && customersByName.length > 0) {
        return customersByName[0].id;
      }

      // Create new customer
      const { data: newCustomer, error: customerError } = await supabase
        .from("customers")
        .insert({
          name: customerName,
          phone: customerPhone || null,
          user_id: user.id,
        })
        .select("id")
        .single();

      if (customerError) throw customerError;
      return newCustomer.id;
    } catch (error) {
      console.error("Error finding/creating customer:", error);
      throw error;
    }
  };

  /**
   * Generate an order number based on user's orderIdConfig
   * Falls back to null if no config or auto-generation disabled
   */
  const generateOrderNumber = async (
    source: OrderSource = 'manual'
  ): Promise<string | null> => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return null;

      // Fetch user's order ID config
      const { data: profile } = await supabase
        .from("profiles")
        .select("settings")
        .eq("id", user.id)
        .single();

      const settings = profile?.settings as UserSettings | null;
      const config = settings?.orderIdConfig || DEFAULT_ORDER_ID_CONFIG;
      const fmt = config.unifiedFormat;
      const today = toZonedTime(new Date(), 'Asia/Tbilisi');

      let sequence: number;

      // Determine sequence strategy based on date format
      if (fmt.dateFormat === 'none') {
        // NO DATE FORMAT: Find MAX sequence across ALL orders with this prefix
        // This prevents duplicate key errors when orders from different days would get same number
        const prefix = config.mode === 'per-source' && config.perSourceFormats?.[source]?.prefix 
          ? config.perSourceFormats[source].prefix 
          : fmt.prefix;
        const separator = fmt.separator || '-';
        const searchPattern = prefix ? `${prefix}${separator}%` : `%`;

        // Fetch recent orders desc to find max sequence efficiently
        // Note: limit(200) prevents hitting default 1000-row cap and reduces payload
        const { data: existingOrders } = await supabase
          .from("orders")
          .select("order_number")
          .eq("user_id", user.id)
          .like("order_number", searchPattern)
          .order("order_number", { ascending: false })
          .limit(200);

        let maxSeq = 0;
        // Build regex to extract sequence number: PREFIX-SEQUENCE (no date)
        const escapedPrefix = (prefix || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const escapedSep = separator.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const seqRegex = new RegExp(`^${escapedPrefix}${escapedSep}(\\d+)$`, 'i');

        for (const order of existingOrders || []) {
          if (order.order_number) {
            const match = order.order_number.match(seqRegex);
            if (match) {
              const num = parseInt(match[1], 10);
              if (num > maxSeq) maxSeq = num;
            }
          }
        }
        sequence = maxSeq + 1;
      } else {
        // WITH DATE FORMAT: Count today's orders in Tbilisi timezone
        const startOfDay = new Date(today);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(today);
        endOfDay.setHours(23, 59, 59, 999);

        const { count } = await supabase
          .from("orders")
          .select("id", { count: 'exact', head: true })
          .eq("user_id", user.id)
          .gte("created_at", startOfDay.toISOString())
          .lte("created_at", endOfDay.toISOString());

        sequence = (count || 0) + 1;
      }

      return buildOrderNumber(config, source, sequence, today);
    } catch (error) {
      console.error("Error generating order number:", error);
      return null;
    }
  };

  /**
   * Create a new manual order
   */
  const createOrder = async (orderData: {
    orderNumber: string;
    customerName: string;
    customerPhone?: string;
    address?: string;
    message: string;
    source: string;
    category: string;
    totalPrice?: string;
    comments?: string;
    csrName?: string;
    parsedItems?: string;
    requestedDeliveryDate?: string | null;
  }) => {
    const MAX_RETRIES = 3;

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) throw new Error("Not authenticated");

        const customerId = await findOrCreateCustomer(
          orderData.customerName,
          orderData.customerPhone
        );

        // Auto-generate order number if not provided (re-generate on retry)
        let orderNumber = attempt === 0 ? (orderData.orderNumber || null) : null;
        if (!orderNumber) {
          orderNumber = await generateOrderNumber('manual');
        }

        const { data, error } = await supabase
          .from("orders")
          .insert({
            order_number: orderNumber,
            customer_name: orderData.customerName,
            customer_phone: orderData.customerPhone || null,
            customer_id: customerId,
            address: orderData.address || null,
            message_raw: orderData.message,
            source: orderData.source,
            category: orderData.category,
            total_price: orderData.totalPrice
              ? parseFloat(orderData.totalPrice)
              : null,
            comments: orderData.comments || null,
            csr_name: orderData.csrName || null,
            parsed_items: orderData.parsedItems || null,
            order_status: ORDER_STATUS.NEW,
            user_id: user.id,
            requested_delivery_date: orderData.requestedDeliveryDate || null,
          })
          .select()
          .single();

        if (error) {
          // Retry on order_number unique constraint violation
          if (error.code === '23505' && attempt < MAX_RETRIES - 1) {
            console.warn(`[createOrder] Order number collision (attempt ${attempt + 1}), retrying...`);
            continue;
          }
          throw error;
        }

        // Write initial activity log so this order appears in the feed
        if (data?.id) {
          try {
            // Detect workspace language from localStorage (same source as LanguageContext)
            const lang = (localStorage.getItem("preferred-language") === "ka" ? "ka" : "en") as "en" | "ka";
            const logEntry = createActivityEntry("system", "order_received", undefined, lang);
            await appendNoteAtomic(data.id, logEntry, false);
          } catch (logError) {
            // Non-fatal: order was created successfully, log entry is nice-to-have
            console.warn("[createOrder] Failed to write initial activity log:", logError);
          }
        }

        return data;
      } catch (error: any) {
        if (error?.code === '23505' && attempt < MAX_RETRIES - 1) {
          console.warn(`[createOrder] Collision retry ${attempt + 1}`);
          continue;
        }
        console.error("Error creating order:", error);
        throw error;
      }
    }
    throw new Error("Failed to create order after retries");
  };

  return {
    checkDuplicateOrder,
    findOrCreateCustomer,
    generateOrderNumber,
    createOrder,
  };
};
